// The data side of a briefing chart: turning a chart's stored lines into
// plottable points, palette slots, legend entries, and the cluster of points
// under the pointer. No React.

import {
  DEFAULT_SERIES_PALETTE,
  seriesPaletteClasses,
} from 'js/components/Charts/SeriesRadarChart/series-radar-utils';

export const DEFAULT_MARGINS = { top: 10, right: 12, bottom: 24, left: 32 };
const CHART_PALETTE_SIZE = 18;
const CLUSTER_MIN_PX = 10;
const CLUSTER_MAX_PX = 40;
export const TEAM_HOVER_MAX_DISTANCE = 16;

export function lineId(line) {
  return line?.id || line?.label;
}

// What a line's *color* is keyed on -- almost always its own id, except an
// "overall" reference line, whose `id` ("org:Measure X") only exists to stay
// unique alongside that measure's own line in the same chart. Its color
// still needs to land on that measure's own palette slot, via the plain
// measure name OrgBriefingCharts sends as `measure`.
function paletteKey(line) {
  return line?.style === 'overall'
    ? line.measure || lineId(line)
    : lineId(line);
}

function paletteClassName(index) {
  return seriesPaletteClasses(
    DEFAULT_SERIES_PALETTE,
    (index % CHART_PALETTE_SIZE) + 1
  ).paletteClassName;
}

// One palette for the whole briefing, keyed by the series that actually
// appear in charts -- first-cited measure or team keeps the same
// palette-chart-N everywhere, rather than re-indexing from 1 in each plot
// or walking the full assessment catalog. A "teams" chart's own team lines
// (see isNeutral below) are the only lines that never render in color, so
// they are the only ones skipped here.
export function chartPaletteById(document) {
  const ids = [];
  const seen = new Set();

  for (const section of document?.sections ?? []) {
    for (const item of section.items ?? []) {
      const chart = item.chart;
      for (const line of chart?.lines ?? []) {
        if (isNeutral(line, chart.kind)) {
          continue;
        }
        const id = paletteKey(line);
        if (id && !seen.has(id)) {
          seen.add(id);
          ids.push(id);
        }
      }
    }
  }

  return Object.fromEntries(
    ids.map((id, index) => [id, paletteClassName(index)])
  );
}

// Grey is reserved for a "teams" chart's own team lines: several named teams
// compared on one measure, the same role OrgTrendsChart's team-level lines
// play (color there is reserved for group/org rollups). Every other line --
// a measure compared across one team, a measure compared org-wide, and the
// org-average reference line in either of those -- keeps its measure's own
// color, so "Measure X" reads as the same color everywhere it appears.
function isNeutral(line, chartKind) {
  return line.style !== 'overall' && chartKind === 'teams';
}

export function lineVisual(line, chartKind, index, paletteById) {
  if (isNeutral(line, chartKind)) {
    return { neutral: true, dashed: false, paletteClassName: null };
  }
  return {
    neutral: false,
    dashed: line.style === 'overall',
    paletteClassName:
      paletteById?.[paletteKey(line)] || paletteClassName(index),
  };
}

// A teams chart paints every team the same grey, so a swatch per team name
// restates one color. Two or more of those lines become a single "Teams"
// entry. A lone team keeps its name, and any colored series (the org
// average, a measure) stays its own item.
export function briefingLegendItems(lines, chartKind, paletteById = {}) {
  const items = [];
  let teamsItem = null;

  lines.forEach((line, index) => {
    const visual = lineVisual(line, chartKind, index, paletteById);
    const id = lineId(line);

    if (visual.neutral) {
      if (!teamsItem) {
        teamsItem = {
          key: id,
          label: line.label,
          visual,
          ids: [id],
          highlightsOnHover: true,
        };
        items.push(teamsItem);
      } else {
        teamsItem.key = 'teams';
        teamsItem.label = 'Teams';
        teamsItem.ids.push(id);
        // The shared swatch no longer names the line under the pointer.
        teamsItem.highlightsOnHover = false;
      }
      return;
    }

    items.push({
      key: id,
      label: line.label,
      visual,
      ids: [id],
      highlightsOnHover: true,
    });
  });

  return items;
}

export function labelForCycle(index, count) {
  return count <= 1 ? '' : `${index + 1}`;
}

// Stored captions were sentence case; title-case on render so older
// documents match. Words that already have an internal capital (eNPS,
// iOS) are left alone so a measure name isn't flattened.
export function titleCaseCaption(title) {
  if (!title) {
    return title;
  }
  return title.replace(/[A-Za-z][A-Za-z']*/g, (word) => {
    if (/[A-Z]/.test(word.slice(1))) {
      return word;
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
}

function parseDate(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

// A chart plots on a real time axis only when every one of its lines carries
// a parseable date for every one of its points -- mixing a dated line with
// an undated one would need two incompatible ideas of "where on the x-axis"
// at once, so any missing date drops the whole chart to the ordinal
// cycle-index axis.
//
// A line arrives as [date, score] pairs, so a point can never lose its date
// to an off-by-one; an unknown date is an explicit null in the pair.
export function buildLines(rawLines) {
  const lines = rawLines.map((line) => {
    const raw = Array.isArray(line.points) ? line.points : [];
    const points = raw
      .map(([date, score], i) => ({
        cycle: i,
        score,
        date: parseDate(date),
      }))
      .filter((point) => typeof point.score === 'number');
    return { ...line, points };
  });

  const useTimeAxis =
    lines.some((line) => line.points.length > 0) &&
    lines.every((line) => line.points.every((point) => point.date != null));

  return { lines: scaleLinesToPercent(lines), useTimeAxis };
}

function uniqueSortedXs(lines, pointX) {
  const xs = [];
  const seen = new Set();

  for (const line of lines) {
    for (const point of line.points) {
      const x = pointX(point);
      if (!Number.isFinite(x) || seen.has(x)) {
        continue;
      }
      seen.add(x);
      xs.push(x);
    }
  }

  xs.sort((a, b) => a - b);
  return xs;
}

function clusterRadiusPx(xs) {
  if (xs.length < 2) {
    return 24;
  }
  const gaps = [];
  for (let i = 1; i < xs.length; i += 1) {
    gaps.push(xs[i] - xs[i - 1]);
  }
  gaps.sort((a, b) => a - b);
  return Math.max(
    CLUSTER_MIN_PX,
    Math.min(gaps[Math.floor(gaps.length / 2)] / 2, CLUSTER_MAX_PX)
  );
}

function nearestPointOnLine(line, targetX, pointX) {
  let best = null;
  let bestDist = Infinity;

  for (const point of line.points) {
    const dist = Math.abs(pointX(point) - targetX);
    if (dist < bestDist) {
      bestDist = dist;
      best = point;
    }
  }

  return best ? { point: best, dist: bestDist } : null;
}

// Pick one x-slice (the nearest plotted x to the pointer), then each line
// contributes at most the point closest to that slice -- and only if it is
// near enough to count as the same cluster. A team assessed on a different
// day is omitted rather than paired with the wrong cycle by array index.
export function clusterLinesAtX(lines, pointerX, pointX) {
  const xs = uniqueSortedXs(lines, pointX);
  if (xs.length === 0) {
    return { targetX: pointerX, entries: [] };
  }

  let targetX = xs[0];
  let best = Math.abs(xs[0] - pointerX);
  for (const x of xs) {
    const dist = Math.abs(x - pointerX);
    if (dist < best) {
      best = dist;
      targetX = x;
    }
  }

  const radius = clusterRadiusPx(xs);
  const entries = [];
  for (const line of lines) {
    const match = nearestPointOnLine(line, targetX, pointX);
    if (match && match.dist <= radius) {
      entries.push({ line, point: match.point });
    }
  }

  return { targetX, entries };
}

export function previousPoint(line, point) {
  const index = line.points.indexOf(point);
  return index > 0 ? line.points[index - 1] : null;
}

// Evidence scores are 0..1. Attached chart lines should already be percent,
// but a briefing stored before that conversion still carries the normalized
// values -- on a 0–100 axis those lines sit on the floor. If every point on
// the chart is in [0, 1], scale the whole plot up once.
function scaleLinesToPercent(lines) {
  const scores = lines.flatMap((line) =>
    line.points.map((point) => point.score)
  );
  if (scores.length === 0 || scores.some((score) => score < 0 || score > 1)) {
    return lines;
  }

  return lines.map((line) => ({
    ...line,
    points: line.points.map((point) => ({
      ...point,
      score: point.score * 100,
    })),
  }));
}

export function formatDelta(delta) {
  if (typeof delta !== 'number') {
    return null;
  }
  const rounded = Math.round(delta);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}
